import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import * as Sentry from "@sentry/nextjs";
import axiosInstance from "src/axios";
import { detectCurrentInAppBrowser } from "src/utils/in-app-browser";
import { getFirebaseAuth } from "./index";

const REPORTED = new WeakSet<object>();

/**
 * True for an error signInWithFirebase has already sent to Sentry, so a caller
 * that reports its own failures does not send it twice.
 */
export function wasReportedAuthError(err: unknown): boolean {
  return typeof err === "object" && err !== null && REPORTED.has(err);
}

/**
 * A Firebase popup failure, reported with its auth code and the in-app browser
 * it happened in. Google refuses OAuth in Facebook/Instagram WebViews, and
 * these failures were mostly swallowed (one FB event in 30 days), so "the
 * Google button does nothing" could not be measured. Errors Sentry's
 * ignoreErrors already drops (popup closed by the user, ...) stay dropped.
 */
function reportFirebaseFailure(err: unknown, provider: FirebaseProvider): void {
  const { app } = detectCurrentInAppBrowser();
  const code = (err as { code?: unknown })?.code;
  Sentry.captureException(err, {
    tags: {
      area: "firebase-auth",
      provider,
      firebaseCode: typeof code === "string" ? code : "unknown",
      inAppBrowser: app ?? "none",
    },
  });
  if (typeof err === "object" && err !== null) REPORTED.add(err);
}

export type FirebaseProvider = "google" | "apple";

export async function signInWithFirebase(
  provider: FirebaseProvider,
): Promise<{ access_token: string; refresh_token: string }> {
  const auth = getFirebaseAuth();
  const oauthProvider =
    provider === "google"
      ? new GoogleAuthProvider()
      : new OAuthProvider("apple.com");

  if (provider === "apple") {
    (oauthProvider as OAuthProvider).addScope("email");
    (oauthProvider as OAuthProvider).addScope("name");
  }

  let idToken: string;
  try {
    const result = await signInWithPopup(auth, oauthProvider);
    idToken = await result.user.getIdToken();
  } catch (err) {
    reportFirebaseFailure(err, provider);
    throw err;
  }

  // Exchange Firebase token for our JWT
  const res = await axiosInstance.post("/auth/firebase-login", { idToken });
  return res.data as { access_token: string; refresh_token: string };
}
